/*
 * Map rank on the Google profile (maprank.php and 15-maprank.js, rebuilt): where our listings show in Google Maps from
 * spots around them, for each search we track. Each Google listing has its own circle, centered on it (owner, 2026-11-02:
 * "it should center on the business profiles, since i have 2, 2 different searches or circles and show individiual stats
 * and combined best"). One search at a spot answers for every listing, so the map can show each listing on its own (the
 * spots in its circle, its own place) or all of them together (every spot, the best of them there).
 *
 * Click a spot for the top 3 there. Any of the last 12 checks can be shown, with what changed since. Then who ranks ahead
 * of us most, with their Google rating and reviews next to ours.
 */
import { useState } from "react";
import {
  GRID_AREA_MILES,
  GRID_DENSITY,
  gridChange,
  gridMilesApart,
  gridSpots,
  rankView,
  rivalCompare,
  type RankCircle,
  type RankGridRow,
  type RankGridsAnswer,
  type RankRival,
  type RankRun,
} from "@steward/shared";
import { useAddGrid, useGridArea, useGrids, useRemoveGrid, useRunGrid } from "../api/marketing";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { Choice, InputText } from "../components/fields";
import { plural, shortDate } from "../components/format";
import { Modal, useToast } from "../components/overlays";
import { Icon } from "../components/Icon";
import { Button, Card, EmptyState, LoadError, Skeleton } from "../components/parts";
import { RankMap, rankBand, rankWord, type RankDot } from "../components/RankMap";

// Where every brand works today, for an area with no listing to start from.
const HOME_BASE = { lat: 29.9511, lng: -90.0715 };
const BEST = "best";

const errText = (e: unknown) => (e instanceof Error ? e.message : "That didn't work.");
/** A listing's key in a run: its id, or "own" for the company's own from before listings were kept apart. */
const whoKey = (w: { id: string | null }) => w.id ?? "own";

type Listings = RankGridsAnswer["listings"];
const pinsOf = (listings: Listings) =>
  listings.flatMap((l) => (l.lat !== null && l.lng !== null ? [{ lat: l.lat, lng: l.lng, name: l.name }] : []));

export function MapRank() {
  const { company, can } = useSession();
  const q = useGrids(company.id);
  const edit = can("marketing.update");
  const [setup, setSetup] = useState(false);
  const [pick, setPick] = useState<string | null>(null);
  // Which listing the map shows, kept while switching searches.
  const [who, setWho] = useState(BEST);
  const checkAll = useCheckAll();
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  const a = q.data;
  const g = a?.grids.find((x) => x.id === pick) ?? a?.grids[0] ?? null;
  return (
    <Card
      title="Map rank"
      action={
        edit && a?.grids.length ? (
          <span className="row tight rank-actions">
            <Button type="tertiary" size="small" icon="sliders" onClick={() => setSetup(true)}>
              Settings
            </Button>
            <Button type="secondary" size="small" loading={checkAll.busy !== null} onClick={() => void checkAll.run(a.grids)}>
              {checkAll.busy ?? "Check all now"}
            </Button>
          </span>
        ) : null
      }
    >
      {!a ? (
        <Skeleton rows={4} />
      ) : !g ? (
        <EmptyState
          icon="pin"
          title="See where you show up on Google Maps"
          action={
            edit ? (
              <Button icon="plus" onClick={() => setSetup(true)}>
                Set up map rank
              </Button>
            ) : null
          }
        >
          Pick the searches people type. We check each one from spots all around each of your Google listings, once a week. Green
          spots are the top 3, the ones most people call.
        </EmptyState>
      ) : (
        <div className="stack">
          {a.grids.length > 1 ? (
            <div className="segmented page-tabs" role="tablist" aria-label="Search">
              {a.grids.map((x) => (
                <button key={x.id} type="button" role="tab" aria-selected={x.id === g.id} onClick={() => setPick(x.id)}>
                  {x.keyword}
                </button>
              ))}
            </div>
          ) : null}
          <GridView key={g.id} g={g} listings={a.listings} who={who} onWho={setWho} />
          <p className="muted small">
            Checked once a week. Close to what the Google Maps app shows. Each phone also weighs its owner&apos;s own searches,
            and ads are left out.
          </p>
        </div>
      )}
      {edit && a && setup ? <SetupForm onClose={() => setSetup(false)} a={a} /> : null}
    </Card>
  );
}

/** Checks every search one after another, saying which one it is on. */
function useCheckAll() {
  const run = useRunGrid();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  return {
    busy,
    run: async (grids: RankGridRow[]) => {
      try {
        for (const [n, x] of grids.entries()) {
          setBusy(`Checking ${String(n + 1)} of ${String(grids.length)}`);
          await run.mutateAsync(x.id);
        }
        toast("Checked");
      } catch (e) {
        toast(errText(e), { kind: "error" });
      } finally {
        setBusy(null);
      }
    },
  };
}

/** A run seen for one listing (by key) or all of them: the spots shown and our place at each. */
function viewOf(run: RankRun, key: string) {
  const k = key === BEST || run.each.length === 0 ? -1 : run.who.findIndex((w) => whoKey(w) === key);
  const v = rankView(run, k < 0 ? null : k);
  return { ...v, k: k < 0 ? null : k, spots: v.idx.map((i) => run.spots[i]!) };
}

function GridView({
  g,
  listings,
  who,
  onWho,
}: {
  g: RankGridRow;
  listings: Listings;
  who: string;
  onWho: (key: string) => void;
}) {
  const { can } = useSession();
  const [at, setAt] = useState(0);
  const [spot, setSpot] = useState<number | null>(null);
  const runGrid = useRunGrid();
  const remove = useRemoveGrid();
  const toast = useToast();
  const run = g.runs[Math.min(at, g.runs.length - 1)];
  if (!run || run.spots.length === 0)
    return (
      <EmptyState
        icon="pin"
        title={`"${g.keyword}" is not checked yet`}
        action={
          can("marketing.update") ? (
            <Button
              loading={runGrid.isPending}
              onClick={() => void runGrid.mutateAsync(g.id).catch((e: unknown) => toast(errText(e), { kind: "error" }))}
            >
              Check now
            </Button>
          ) : null
        }
      >
        A check looks from every spot in your area. It takes a few seconds.
      </EmptyState>
    );
  const many = run.who.length > 1 && run.each.length > 0;
  const key = many ? who : BEST;
  const view = viewOf(run, key);
  const older = g.runs[at + 1] ?? null;
  // An older check from before this listing was kept apart has nothing to compare with it.
  const was = older && (key === BEST || older.who.some((w) => whoKey(w) === key)) ? viewOf(older, key) : null;
  const n = view.points.length;
  const dots: RankDot[] = view.idx.map((i, j) => ({
    i,
    lat: run.spots[i]![0],
    lng: run.spots[i]![1],
    rank: view.points[j] ?? null,
  }));
  const move = was ? Math.round((was.summary.average - view.summary.average) * 10) / 10 : 0;
  const change = was ? gridChange(was, view) : null;
  const latest = g.latest;
  // Each listing's pin is the middle of its circle at the time of the check (where it was dragged, if it was).
  const fromRun = run.circles.length === run.who.length && run.who.length > 0;
  const pins = fromRun
    ? run.who.flatMap((w, j) =>
        view.k === null || view.k === j ? [{ lat: run.circles[j]!.lat, lng: run.circles[j]!.lng, name: w.name }] : [],
      )
    : pinsOf(listings);
  return (
    <div className="stack">
      {many ? (
        <div className="segmented page-tabs" role="tablist" aria-label="Listing">
          <button type="button" role="tab" aria-selected={key === BEST} onClick={() => onWho(BEST)}>
            All listings, best spot
          </button>
          {run.who.map((w) => (
            <button
              key={whoKey(w)}
              type="button"
              role="tab"
              aria-selected={key === whoKey(w)}
              onClick={() => {
                onWho(whoKey(w));
                setSpot(null);
              }}
            >
              {w.name}
            </button>
          ))}
        </div>
      ) : null}
      <div className="rank-stats">
        <Stat
          label="Average place"
          value={`#${view.summary.average.toFixed(1)}`}
          hint={
            !was || !older
              ? "Lower is better"
              : move > 0
                ? `Up ${move.toFixed(1)} since ${shortDate(older.runOn)}`
                : move < 0
                  ? `Down ${Math.abs(move).toFixed(1)} since ${shortDate(older.runOn)}`
                  : `Same as ${shortDate(older.runOn)}`
          }
          tone={move > 0 ? "up" : move < 0 ? "down" : undefined}
        />
        <Stat label="In the top 3" value={String(view.summary.top3)} hint={`of ${plural(n, "spot")}`} />
        <Stat label="On the first page" value={String(view.summary.found)} hint={`Top 20, of ${plural(n, "spot")}`} />
        <Stat label="Checked" value={shortDate(run.runOn)} hint={at === 0 ? "Newest check" : "An older check"} />
      </div>
      {many && key === BEST ? <ListingStats run={run} /> : null}
      <div className="rank-layout">
        <RankMap
          center={{ lat: g.lat, lng: g.lng }}
          dots={dots}
          pins={pins}
          picked={spot}
          onDot={(i) => setSpot(spot === i ? null : i)}
          label={`Where you show for ${g.keyword} at each spot`}
        />
        <div className="stack rank-side">
          <SpotDetail run={run} spot={spot} k={view.k} />
          <div className="rank-key small" aria-label="What the colors mean">
            <span>
              <i className="rank-dot top" /> 1 to 3
            </span>
            <span>
              <i className="rank-dot mid" /> 4 to 10
            </span>
            <span>
              <i className="rank-dot low" /> 11 to 20
            </span>
            <span>
              <i className="rank-dot miss" /> Not in the top 20
            </span>
          </div>
          {change && older ? (
            <p className="small">
              Since {shortDate(older.runOn)}, better at <b>{change.better}</b>, worse at <b>{change.worse}</b>, the same at{" "}
              {change.same}.
            </p>
          ) : null}
          {g.runs.length > 1 ? (
            <Runs
              runs={g.runs}
              at={at}
              onPick={(i) => {
                setAt(i);
                setSpot(null);
              }}
            />
          ) : null}
        </div>
      </div>
      {at === 0 && latest?.rivals.length ? <Rivals rivals={latest.rivals} us={latest.us} average={latest.average} /> : null}
      {can("marketing.update") ? (
        <div className="row end">
          <Button
            type="tertiary"
            size="small"
            variation="destructive"
            loading={remove.isPending}
            onClick={() =>
              void remove
                .mutateAsync(g.id)
                .then(() => toast(`Stopped tracking "${g.keyword}"`))
                .catch((e: unknown) => toast(errText(e), { kind: "error" }))
            }
          >
            Stop tracking this search
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Each listing on its own, in its own circle, under the combined numbers. */
function ListingStats({ run }: { run: RankRun }) {
  return (
    <div className="rank-listings">
      {run.who.map((w, k) => {
        const s = rankView(run, k).summary;
        return (
          <div key={whoKey(w)} className="rank-listing">
            <b>{w.name}</b>
            <span className="muted small">
              Average #{s.average.toFixed(1)}, top 3 at {s.top3}, first page at {s.found}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint: string; tone?: "up" | "down" | undefined }) {
  return (
    <div className="rank-stat">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      <span className={`hint${tone ? ` mk-${tone}` : ""}`}>{hint}</span>
    </div>
  );
}

/** The spot clicked: our place there (one listing's, or the best and each one's) and the top 3, ours in bold. */
function SpotDetail({ run, spot, k }: { run: RankRun; spot: number | null; k: number | null }) {
  if (spot === null || spot >= run.points.length) return <p className="muted">Click a spot to see who shows up first there.</p>;
  const me = k === null ? (run.points[spot] ?? null) : (run.each[spot]?.[k] ?? null);
  const top = run.tops[spot] ?? [];
  const each = run.each[spot] ?? [];
  const ours = (name: string) => run.who.some((w) => w.name.toLowerCase() === name.toLowerCase());
  return (
    <div className="rank-spot">
      <b>
        <i className={`rank-dot ${rankBand(me)}`}>{rankWord(me)}</i>
        {me === null ? "You are not in the top 20 here" : `You are number ${String(me)} here`}
      </b>
      {k === null && run.who.length > 1 && each.length ? (
        <span className="small">
          {run.who
            .map((w, j) => `${w.name} ${each[j] === null || each[j] === undefined ? "not in the top 20" : `#${String(each[j])}`}`)
            .join(". ")}
          .
        </span>
      ) : null}
      {top.length ? (
        <ol>
          {top.map((name, i) => (
            <li key={`${String(i)}${name}`}>{ours(name) || me === i + 1 ? <b>{name} (you)</b> : name}</li>
          ))}
        </ol>
      ) : (
        <span className="muted small">The top 3 show from the next check.</span>
      )}
    </div>
  );
}

function Runs({ runs, at, onPick }: { runs: RankRun[]; at: number; onPick: (i: number) => void }) {
  return (
    <div className="stack tight">
      <b className="small">Past checks</b>
      <div className="rank-runs" role="listbox" aria-label="Past checks">
        {runs.map((r, i) => (
          <button key={r.runOn} type="button" role="option" aria-selected={i === at} onClick={() => onPick(i)}>
            <span>{shortDate(r.runOn)}</span>
            <span className="muted">
              #{r.average.toFixed(1)}, top 3 at {r.top3}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

type RivalRow = RankRival & { you: boolean };

/** Who ranks ahead of us in the newest check, with Google rating and reviews, and ours to compare (maprank-comp.php). */
function Rivals({
  rivals,
  us,
  average,
}: {
  rivals: RankRival[];
  us: { rating: number | null; reviews: number | null } | null;
  average: number;
}) {
  const rows: RivalRow[] = [
    ...(us ? [{ name: "You", ahead: 0, best: 0, average, rating: us.rating, reviews: us.reviews, you: true }] : []),
    ...rivals.map((r) => ({ ...r, you: false })),
  ];
  const line = rivalCompare(us, rivals);
  return (
    <div className="stack mk-rivals">
      <DataTable
        title="Who ranks ahead of you"
        rows={rows}
        rowKey={(r) => (r.you ? "you" : r.name)}
        rowClass={(r) => (r.you ? "mk-you" : undefined)}
        empty="No one ranks ahead of you here"
        columns={[
          { key: "n", header: "Business", render: (r) => (r.you ? <b>You</b> : r.name) },
          {
            key: "r",
            header: "Rating",
            align: "right",
            render: (r) => (r.rating === null ? "" : `${r.rating.toFixed(1)} stars`),
          },
          { key: "v", header: "Reviews", align: "right", render: (r) => (r.reviews === null ? "" : r.reviews.toLocaleString()) },
          { key: "a", header: "Ahead of you at", align: "right", render: (r) => (r.you ? "" : plural(r.ahead, "spot")) },
          { key: "p", header: "Average place", align: "right", render: (r) => r.average.toFixed(1) },
        ]}
      />
      {line ? <p className="muted">{line}</p> : null}
      <p className="muted small">Google numbers from the newest check. Only the top {rivals.length} are looked up.</p>
    </div>
  );
}

/** A row of things to pick more than one of, each with a check box, styled like the single choices above them. */
function Picks({
  label,
  hint,
  options,
  picked,
  onToggle,
}: {
  label: string;
  hint: string;
  options: { value: string; label: string }[];
  picked: (value: string) => boolean;
  onToggle: (value: string) => void;
}) {
  return (
    <div className="field">
      <span className="label">{label}</span>
      <div className="choice picks" role="group" aria-label={label}>
        {options.map((o) => {
          const on = picked(o.value);
          return (
            <button key={o.value} type="button" role="checkbox" aria-checked={on} onClick={() => onToggle(o.value)}>
              <span className="box" aria-hidden="true">
                {on ? <Icon name="check" size={12} /> : null}
              </span>
              <b>{o.label}</b>
            </button>
          );
        })}
      </div>
      <span className="description">{hint}</span>
    </div>
  );
}

/** The circles to start from: the saved ones, else one for each listing on its own spot (or the first one's). */
function startCircles(a: RankGridsAnswer): RankCircle[] {
  const saved = a.grids[0]?.areas;
  if (saved?.length) {
    // A saved circle for the company's own (from before listings were kept apart) becomes the main listing's.
    const main = a.listings[0]?.id ?? null;
    return saved.map((c) => (c.listing === null && main && !saved.some((x) => x.listing === main) ? { ...c, listing: main } : c));
  }
  const at0 = a.listings.find((l) => l.lat !== null && l.lng !== null);
  const fallback = at0 ? { lat: at0.lat!, lng: at0.lng! } : HOME_BASE;
  if (a.listings.length === 0) return [{ listing: null, ...fallback, size: 9, radius: 15, skip: [] }];
  return a.listings.map((l) => ({
    listing: l.id,
    lat: l.lat ?? fallback.lat,
    lng: l.lng ?? fallback.lng,
    size: 9,
    radius: 10,
    skip: [],
  }));
}

/**
 * The circles and the searches, set together. A circle for each listing checked: pick the listing, drag its middle pin,
 * pick how far and how close, click a spot to turn it off or on. Searches are picked from the trade's list or typed.
 * Saving moves every search to the circles, then adds and checks the new searches one at a time.
 */
function SetupForm({ onClose, a }: { onClose: () => void; a: RankGridsAnswer }) {
  const first = a.grids[0];
  const [circles, setCircles] = useState<RankCircle[]>(() => startCircles(a));
  const [on, setOn] = useState(0);
  const tracked = a.grids.map((g) => g.keyword);
  const [kws, setKws] = useState<string[]>(first ? tracked : a.suggested.slice(0, 3));
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const addGrid = useAddGrid();
  const putArea = useGridArea();
  const remove = useRemoveGrid();
  const toast = useToast();

  const area = circles[Math.min(on, circles.length - 1)]!;
  const nameOf = (c: RankCircle) => a.listings.find((l) => l.id === c.listing)?.name ?? "Your business";
  const miles = gridMilesApart(area.radius, area.size);
  const all = gridSpots(area.lat, area.lng, area.size, miles, area.radius);
  const off = new Set(area.skip);
  const left = all.filter((s) => !off.has(s.i)).length;
  const dots: RankDot[] = all.map((s) => ({ ...s, off: off.has(s.i) }));
  const choices = [...new Set([...tracked, ...a.suggested, ...kws])];
  const same = !!first && JSON.stringify(first.areas) === JSON.stringify(circles);
  const put = (next: Partial<RankCircle>) => setCircles(circles.map((c, i) => (i === on ? { ...c, ...next } : c)));
  // A new middle, spacing or distance is a new set of spots: the old turned off ones no longer line up.
  const reshape = (next: Partial<RankCircle>) => put({ ...next, skip: [] });
  const listed = new Set(circles.map((c) => c.listing));
  const spotOf = (id: string) => {
    const l = a.listings.find((x) => x.id === id);
    return l?.lat != null && l.lng !== null ? { lat: l.lat, lng: l.lng } : { lat: area.lat, lng: area.lng };
  };
  const toggleListing = (id: string) => {
    if (listed.has(id)) {
      if (circles.length === 1) return toast("Check at least one listing.", { kind: "error" });
      setCircles(circles.filter((c) => c.listing !== id));
      setOn(0);
      return;
    }
    setCircles([...circles, { listing: id, ...spotOf(id), size: 9, radius: 10, skip: [] }]);
    setOn(circles.length);
  };

  const add = () => {
    const k = typed.trim().replace(/\s+/g, " ");
    if (!k) return;
    if (!kws.some((x) => x.toLowerCase() === k.toLowerCase())) setKws([...kws, k]);
    setTyped("");
  };

  const save = async () => {
    if (kws.length === 0) return toast("Pick at least one search.", { kind: "error" });
    if (kws.length > 8) return toast("Track 8 searches at most.", { kind: "error" });
    const empty = circles.find(
      (c) => gridSpots(c.lat, c.lng, c.size, gridMilesApart(c.radius, c.size), c.radius, c.skip).length === 0,
    );
    if (empty) return toast(`Leave at least one spot on for ${nameOf(empty)}.`, { kind: "error" });
    const lower = kws.map((k) => k.toLowerCase());
    const gone = a.grids.filter((g) => !lower.includes(g.keyword.toLowerCase()));
    const fresh = kws.filter((k) => !tracked.some((t) => t.toLowerCase() === k.toLowerCase()));
    try {
      setBusy("Saving");
      for (const g of gone) await remove.mutateAsync(g.id);
      if (a.grids.length > gone.length && !same) await putArea.mutateAsync(circles);
      for (const [n, keyword] of fresh.entries()) {
        setBusy(`Checking ${String(n + 1)} of ${String(fresh.length)}`);
        await addGrid.mutateAsync({ keyword, areas: circles });
      }
      toast(fresh.length ? "Saved and checked" : same ? "Saved" : "Saved. The next check uses the new areas.");
      onClose();
    } catch (e) {
      toast(errText(e), { kind: "error" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal
      title="Map rank areas and searches"
      open
      onClose={onClose}
      size="large"
      footer={
        <>
          <Button type="tertiary" onClick={onClose} disabled={busy !== null}>
            Cancel
          </Button>
          <Button loading={busy !== null} onClick={() => void save()}>
            {busy ?? (first ? "Save" : "Save and check now")}
          </Button>
        </>
      }
    >
      <div className="stack">
        <h3 className="rank-setup-head">Where to check</h3>
        {a.listings.length > 1 ? (
          <Picks
            label="Listings to check"
            hint="Each listing gets its own circle around it. One check covers all of them."
            options={a.listings.map((l) => ({ value: l.id, label: l.name }))}
            picked={(id) => listed.has(id)}
            onToggle={toggleListing}
          />
        ) : null}
        <div className="stack tight">
          <b>{circles.length > 1 ? "Each listing's area" : "Your area"}</b>
          <span className="muted small">
            Each area starts on the listing&apos;s Google pin. Drag the pin to move it. Click a spot to turn it off, like one on
            the water, and again to turn it back on. Moving the pin or changing how far or how close turns every spot back on.
          </span>
        </div>
        {circles.length > 1 ? (
          <div className="segmented page-tabs" role="tablist" aria-label="Area to set">
            {circles.map((c, i) => (
              <button key={c.listing ?? "own"} type="button" role="tab" aria-selected={i === on} onClick={() => setOn(i)}>
                {nameOf(c)}
              </button>
            ))}
          </div>
        ) : null}
        <RankMap
          center={{ lat: area.lat, lng: area.lng }}
          dots={dots}
          pins={[
            // Each circle's middle is its listing's pin; the one being set drags.
            ...circles.map((c, i) => ({ lat: c.lat, lng: c.lng, name: nameOf(c), drag: i === on })),
            ...pinsOf(a.listings.filter((l) => !listed.has(l.id))),
          ]}
          circles={circles.map((c, i) => ({
            lat: c.lat,
            lng: c.lng,
            miles: c.radius + 0.35 * gridMilesApart(c.radius, c.size),
            active: i === on,
          }))}
          onMove={(at) => reshape({ lat: Math.round(at.lat * 1e6) / 1e6, lng: Math.round(at.lng * 1e6) / 1e6 })}
          onDot={(i) => put({ skip: off.has(i) ? area.skip.filter((x) => x !== i) : [...area.skip, i].sort((x, y) => x - y) })}
          label="Map of the area to check"
        />
        <div className="row between">
          <span className="muted small">
            {plural(left, "spot")}, about {miles.toFixed(1)} miles apart.
            {off.size ? ` ${plural(off.size, "spot")} turned off.` : ""}
          </span>
          {area.listing && (spotOf(area.listing).lat !== area.lat || spotOf(area.listing).lng !== area.lng) ? (
            <Button type="tertiary" size="small" icon="pin" onClick={() => reshape(spotOf(area.listing!))}>
              Back to the Google pin
            </Button>
          ) : null}
        </div>
        <Choice
          label="How far you go"
          value={String(area.radius)}
          onChange={(v) => reshape({ radius: Number(v) })}
          options={GRID_AREA_MILES.map((m) => ({ value: String(m), label: `${String(m)} miles` }))}
        />
        <Choice
          label="How close the spots are"
          value={String(area.size)}
          onChange={(v) => reshape({ size: Number(v) })}
          options={GRID_DENSITY.map((d) => ({ value: String(d.size), label: d.label }))}
        />
        <h3 className="rank-setup-head">What to search</h3>
        <Picks
          label={`Searches to check, ${String(kws.length)} of 8 picked`}
          hint={`Use the words customers type, like "${a.suggested[0] ?? "house cleaning"}".`}
          options={choices.map((k) => ({ value: k, label: k }))}
          picked={(k) => kws.some((x) => x.toLowerCase() === k.toLowerCase())}
          onToggle={(k) =>
            setKws(
              kws.some((x) => x.toLowerCase() === k.toLowerCase())
                ? kws.filter((x) => x.toLowerCase() !== k.toLowerCase())
                : [...kws, k],
            )
          }
        />
        <div className="rank-add">
          <InputText
            label="Another search"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
          />
          <Button type="secondary" icon="plus" onClick={add} disabled={!typed.trim()}>
            Add
          </Button>
        </div>
        {a.grids.some((g) => !kws.some((k) => k.toLowerCase() === g.keyword.toLowerCase())) ? (
          <p className="small mk-down">Searches you turn off stop being checked.</p>
        ) : null}
      </div>
    </Modal>
  );
}
